import { FilterTarget, GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type {
  CompositeFilter,
  Filter,
  FilterValue,
  SimpleFilter,
  ValueFilter,
} from '@/serverSideTable/materializedTables/materializedTables.constants';

export function composeFilters(
  operation: string,
  filter: Filter,
  ...filters: (Filter | null)[]
): Filter | CompositeFilter {
  const validFilters = filters.filter((filter) => filter) as Filter[];
  if (validFilters.length === 0) {
    return filter;
  } else if (validFilters.length === 1) {
    return { compositeFilter: { operation, filter1: filter, filter2: validFilters[0] } };
  } else {
    return validFilters.reduce(
      (compositeFilter, newFilter) => composeFilters(operation, compositeFilter, newFilter),
      filter,
    );
  }
}

export function andFilters(filter: Filter, ...filters: (Filter | null)[]): Filter | CompositeFilter {
  return composeFilters(GraphQL.Operations.And, filter, ...filters);
}

export function orFilters(filter: Filter, ...filters: (Filter | null)[]): Filter | CompositeFilter {
  return composeFilters(GraphQL.Operations.Or, filter, ...filters);
}

export function valueFilter(
  columnName: string,
  filterType: string,
  value: FilterValue | FilterValue[],
  filterTarget?: FilterTarget,
  filterTargetColumn?: string,
): ValueFilter {
  return {
    valueFilter: {
      columnName,
      filterType,
      value,
      ...(filterTarget !== undefined && { filterTarget }),
      ...(filterTargetColumn !== undefined && { filterTargetColumn }),
    },
  };
}

export function simpleFilter(
  columnName: string,
  filterType: string,
  filterTarget?: FilterTarget,
  filterTargetColumn?: string,
): SimpleFilter {
  return {
    simpleFilter: {
      columnName,
      filterType,
      ...(filterTarget !== undefined && { filterTarget }),
      ...(filterTargetColumn !== undefined && { filterTargetColumn }),
    },
  };
}

export function isNull(columnName: string): SimpleFilter {
  return simpleFilter(columnName, GraphQL.FilterTypes.IsNull);
}

export function isNotNull(columnName: string): SimpleFilter {
  return simpleFilter(columnName, GraphQL.FilterTypes.IsNotNull);
}

export function notEqual(columnName: string, value: FilterValue): ValueFilter {
  return valueFilter(columnName, GraphQL.FilterTypes.NotIn, [value]);
}

export function equal(columnName: string, value: FilterValue): ValueFilter {
  return valueFilter(columnName, GraphQL.FilterTypes.In, [value]);
}

function getFilterTarget(filter: Filter): FilterTarget | undefined {
  if ('valueFilter' in filter) {
    return filter.valueFilter.filterTarget;
  }
  if ('simpleFilter' in filter) {
    return filter.simpleFilter.filterTarget;
  }
  return undefined;
}

/**
 * Recursively removes filters with AGGREGATED target from a filter tree.
 * At the leaf level (expanding a group to see child rows), grouped-mode filters are not
 * applicable as there is no grouping column to aggregate on.
 * For composite filters, if one side is stripped the other side is returned;
 * if both sides are stripped, returns null.
 */
export function stripAggregatedFilters(filter: Filter | null): Filter | null {
  if (!filter) {
    return null;
  }

  if ('compositeFilter' in filter) {
    const stripped1 = stripAggregatedFilters(filter.compositeFilter.filter1);
    const stripped2 = stripAggregatedFilters(filter.compositeFilter.filter2);

    if (!stripped1 && !stripped2) {
      return null;
    }
    if (!stripped1) {
      return stripped2;
    }
    if (!stripped2) {
      return stripped1;
    }

    return { compositeFilter: { ...filter.compositeFilter, filter1: stripped1, filter2: stripped2 } };
  }

  const target = getFilterTarget(filter);
  if (target === FilterTarget.AGGREGATED) {
    return null;
  }

  return filter;
}
